/**
@format
@flow
*/
import React from 'react';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { useRouteMatch } from 'react-router';
import styled from 'styled-components';
import Colors from '@ziynx/theme/colors';
import Fonts from '@ziynx/components/typography/fonts';
import { bodyConfig } from '@ziynx/components/typography';

type Props = $ReadOnly<{|
  /**
   * Extra Small
   */
  extraSmall?: boolean,

  /**
   * Label for the NavLink
   */
  label?: string,

  /**
   * Routing path prop for React Router NavLink
   */
  to?: string | {
    pathname: string,
    search?: string, // Query parameters ex. ?query=true
    hash?: string, // ex '#hash'
    state: Object // ex  { isAdmin: true }
  },

  /**
   * Custom color, if any
   * Defaults to PrimaryBlue
   */
  color?: string,

  /**
   * Indicates link is active when it's exact
   */
  activeOnlyWhenExact?: boolean,

  /**
   * Indicates if link is active
   */
  isActive?: () => void,

  /**
   * Children, if present
   */
  children?: any,
|}>;

const LinkComponent = styled(RouterNavLink)`
  align-items: center;
  text-decoration: none;
  color: ${({ color }) => color};
`;

const LinkWrapper = styled.span`
  font-family: ${Fonts.Nunito};
  font-size: ${({ extraSmall }) => (extraSmall ? bodyConfig.extraSmall.fontSize : bodyConfig.medium.fontSize)}px;
  line-height: ${({ extraSmall }) => (extraSmall ? bodyConfig.extraSmall.lineHeight : bodyConfig.medium.lineHeight)}px;
  font-weight: ${bodyConfig.fontWeight};
`;

/**
 * Temporary Link component
 */
const NavLink = (props: Props): React$Node => {
  const {
    label,
    to,
    activeOnlyWhenExact,
    extraSmall,
    children,
    color,
    ...rest
  } = props;

  const match = useRouteMatch({
    path: to,
    exact: activeOnlyWhenExact,
  });
  return (
    <LinkWrapper className={match ? 'active' : 'inactive'} extraSmall={extraSmall}>
      <LinkComponent to={to} color={color} {...rest}>
        {children || label}
      </LinkComponent>
    </LinkWrapper>
  );
};

NavLink.defaultProps = {
  label: '',
  to: '/',
  color: Colors.PrimaryBlue,
  activeOnlyWhenExact: true,
};

export default NavLink;
